// src/components/dashboard/ElectricityDemandFromDB.js
import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useElectricityGenTS } from "../../hooks/useElectricityGenTS";
import { DASHBOARD_COLORS, getNetZeroColor } from "../../config/colorScheme";

function toPercentChange(labels, values, baselineYear = "2025") {
  if (!labels?.length || !values?.length) return [];
  const idx = labels.indexOf(String(baselineYear));
  const base = idx >= 0 ? values[idx] : values[0];
  if (!base || !isFinite(base)) return values.map(() => 0);
  return values.map((v) => ((v - base) / base) * 100);
}

export default function ElectricityDemandFromDB({
  scenario,                             // required (primary)
  comparisonScenario = null,            // optional (string key)
  comparisonScenarioKey = null,         // optional (alternate prop name)
  comparisonLabel,                      // optional label for overlay
  from = 2025,
  to = 2050,
  unitScale = 0.277778,                 // convert to TWh if needed
  title = "UK Electricity Demand Projection (2025–2050)",
  showPercent = false,                  // set true if you want the % series + y1 axis
  baselineYear = "2025",                // for % calcs and BAU flat line
}) {
  // accept either prop name from the parent
  const compKey = comparisonScenario || comparisonScenarioKey || undefined;

  const primary = useElectricityGenTS({ scenario, from, to });
  const comp    = useElectricityGenTS({ scenario: compKey, from, to });

  // Labels: prefer primary, fall back to comparison
  const labels = primary.labels?.length ? primary.labels : comp.labels;

  // Align helper + scale
  const align = (lbls, srcLabels, srcValues) => {
    const map = new Map((srcLabels ?? []).map((l, i) => [String(l), srcValues?.[i] ?? 0]));
    return (lbls ?? []).map((l) => (map.get(String(l)) ?? 0) * unitScale);
  };

  const yourVals = useMemo(
    () => align(labels, primary.labels, primary.totalsByYear),
    [labels, primary.labels, primary.totalsByYear]
  );

  const compVals = useMemo(
    () => align(labels, comp.labels, comp.totalsByYear),
    [labels, comp.labels, comp.totalsByYear]
  );

  // % change vs baselineYear
  const yourPct = useMemo(() => toPercentChange(labels, yourVals, baselineYear), [labels, yourVals, baselineYear]);
  const compPct = useMemo(() => toPercentChange(labels, compVals, baselineYear), [labels, compVals, baselineYear]);

  // Flat BAU from baseline year (from primary)
  const bauFlat = useMemo(() => {
    if (!labels?.length || !yourVals?.length) return [];
    const idx = labels.indexOf(String(baselineYear));
    const base = idx >= 0 ? yourVals[idx] : yourVals[0];
    return labels.map(() => base ?? 0);
  }, [labels, yourVals, baselineYear]);

  const datasets = useMemo(() => {
    const ds = [];

    if (bauFlat.length) {
      ds.push({
        label: `${baselineYear} demand level`,
        data: bauFlat,
        borderColor: DASHBOARD_COLORS?.neutral?.baseline?.primary ?? "#6b7280",
        backgroundColor: DASHBOARD_COLORS?.neutral?.baseline?.light ?? "rgba(107,114,128,0.15)",
        tension: 0.3,
        fill: false,
        yAxisID: "y",
        pointRadius: 0,
      });
    }

    // Your scenario (absolute)
    ds.push({
      label: "Your Scenario",
      data: yourVals,
      borderColor: getNetZeroColor?.("energyDemand", "primary") ?? "#2563eb",
      backgroundColor: getNetZeroColor?.("energyDemand", "light") ?? "rgba(37,99,235,0.15)",
      tension: 0.3,
      fill: false,
      yAxisID: "y",
      pointRadius: 0,
    });

    if (showPercent) {
      ds.push({
        label: "Your Scenario: % change vs " + baselineYear,
        data: yourPct,
        borderColor: "#2563eb",
        backgroundColor: "rgba(37,99,235,0.08)",
        tension: 0.3,
        fill: false,
        yAxisID: "y1",
        borderDash: [6, 4],
        pointRadius: 0,
      });
    }

    // Comparison overlay (absolute)
    if (compKey && compVals?.some((v) => v !== 0)) {
      ds.push({
        label: comparisonLabel || "Comparison",
        data: compVals,
        borderColor: "#8b5cf6",
        backgroundColor: "rgba(139,92,246,0.10)",
        tension: 0.3,
        fill: false,
        yAxisID: "y",
        borderDash: [5, 5],
        pointRadius: 0,
        borderWidth: 2,
      });

      if (showPercent) {
        ds.push({
          label: (comparisonLabel || "Comparison") + `: % change vs ${baselineYear}`,
          data: compPct,
          borderColor: "#8b5cf6",
          backgroundColor: "rgba(139,92,246,0.06)",
          tension: 0.3,
          fill: false,
          yAxisID: "y1",
          borderDash: [2, 3],
          pointRadius: 0,
        });
      }
    }

    return ds;
  }, [bauFlat, yourVals, yourPct, compVals, compPct, compKey, comparisonLabel, baselineYear, showPercent]);

  const options = useMemo(() => {
    const hasY1 = showPercent; // only add y1 axis if we’re plotting % series
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "top" },
        title: { display: true, text: title },
        tooltip: {
          callbacks: {
            label: (ctx) =>
              ctx.dataset.yAxisID === "y1"
                ? `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%`
                : `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString(undefined, { maximumFractionDigits: 0 })} TWh`,
          },
        },
      },
      scales: {
        x: { title: { display: true, text: "Year" } },
        y: {
          type: "linear",
          position: "left",
          title: { display: true, text: "Annual Electricity Demand (TWh)" },
          beginAtZero: false,
          min: 0,
          max: 750,
        },
        ...(hasY1 && {
          y1: {
            type: "linear",
            position: "right",
            title: { display: true, text: `% Change from ${baselineYear}` },
            grid: { drawOnChartArea: false },
            ticks: { callback: (v) => `${v}%` },
          },
        }),
      },
    };
  }, [title, showPercent, baselineYear]);

  const loading = primary.loading || (!!compKey && comp.loading);
  const error = primary.error || (!!compKey && comp.error);

  return (
    <div className="w-full h-96">
      {loading ? (
        <div className="w-full h-full grid place-items-center">Loading electricity data…</div>
      ) : error ? (
        <div className="w-full h-full grid place-items-center text-red-600">Error: {error.message}</div>
      ) : (
        <Line data={{ labels, datasets }} options={options} />
      )}
    </div>
  );
}
